import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Loader2, Package } from "lucide-react";
import {
  ContestSummaryProduct,
  ContestSummarySale,
  ContestSummarySalesType,
  getContestSummarySales,
} from "@/Api/stocks";
import { Dialog } from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AppDialogBody, AppDialogContent } from "@/components/ui/app-dialog";
import { getSaleStatusBadge } from "@/constants/saleStatus";
import { cn } from "@/lib/utils";
import { Mask } from "@/utils/mask";

type PendingDistribution = ContestSummaryProduct["pending_distributions"][number];

export type SummaryGroupModalType = ContestSummarySalesType | "pending";

export type SummaryGroupModalTarget = {
  type: SummaryGroupModalType;
  productClassificationId: number;
  description: string;
  /** Só para "pending": já vêm no payload do resumo */
  pendingDistributions?: PendingDistribution[];
};

type SummaryGroupModalProps = {
  target: SummaryGroupModalTarget | null;
  onClose: () => void;
  contestId: number | null;
  unityId?: number | null;
  unityName?: string | null;
  contestName?: string;
  lotteryName?: string;
};

const TITLES: Record<SummaryGroupModalType, { title: string; hint: string; tone: string }> = {
  queue: {
    title: "Solicitados na Fila Pré-venda",
    hint: "Pedidos que ainda não foram aceitos",
    tone: "bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300",
  },
  in_progress: {
    title: "Vendas em andamento",
    hint: "Cotas com vendas em andamento",
    tone: "bg-amber-50 text-amber-700 dark:bg-amber-900/20 dark:text-amber-300",
  },
  sold: {
    title: "Vendidos",
    hint: "Cotas efetivamente vendidas",
    tone: "bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-300",
  },
  pending: {
    title: "Não aceitos",
    hint: "Cotas distribuídas aguardando aceitação",
    tone: "bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-300",
  },
};

// created_at vem em UTC e sem fuso; interpreta como UTC e mostra no horário local
const formatDateTime = (value: string | null | undefined) => {
  if (!value) return "-";
  const hasZone = /[zZ]|[+-]\d{2}:?\d{2}$/.test(value);
  const date = new Date(hasZone ? value : `${value.replace(" ", "T")}Z`);
  return Number.isNaN(date.getTime())
    ? "-"
    : new Intl.DateTimeFormat("pt-BR", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      }).format(date);
};

const formatCotas = (quantity: number) => `${quantity} ${quantity === 1 ? "cota" : "cotas"}`;

export const SummaryGroupModal = ({
  target,
  onClose,
  contestId,
  unityId,
  unityName,
  contestName,
  lotteryName,
}: SummaryGroupModalProps) => {
  const [sales, setSales] = useState<ContestSummarySale[]>([]);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);

  const type = target?.type;
  const productClassificationId = target?.productClassificationId;

  useEffect(() => {
    if (!type || type === "pending" || !contestId || !productClassificationId) return;

    let cancelled = false;
    setLoading(true);
    setFailed(false);
    setSales([]);

    (async () => {
      try {
        const { data, ok } = await getContestSummarySales(
          contestId,
          productClassificationId,
          type,
          unityId
        );
        if (cancelled) return;
        if (!ok || !data?.response) {
          setFailed(true);
          return;
        }
        setSales(data.response.sales || []);
        setTruncated(!!data.response.truncated);
      } catch (error) {
        console.error("Erro ao buscar vendas do resumo:", error);
        if (!cancelled) setFailed(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [type, productClassificationId, contestId, unityId]);

  if (!target) return null;

  const config = TITLES[target.type];
  const isPending = target.type === "pending";
  const distributions = target.pendingDistributions || [];
  const quantity = isPending
    ? distributions.reduce((sum, d) => sum + (d.quota_quantity || 0), 0)
    : sales.reduce((sum, sale) => sum + (sale.quantity || 0), 0);
  const totalValue = isPending
    ? 0
    : sales.reduce((sum, sale) => sum + (sale.product_price_total || 0), 0);
  const empty = isPending ? distributions.length === 0 : !loading && !failed && sales.length === 0;

  const subtitle = [
    lotteryName && contestName ? `${lotteryName} ${contestName}` : contestName,
    unityName,
    target.description,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <AppDialogContent
        icon={Package}
        title={config.title}
        description={subtitle}
        className="sm:max-w-5xl"
      >
        <AppDialogBody>
          <div className={cn("rounded-lg p-4", config.tone)}>
            <div className="text-sm">{config.hint}</div>
            <div className="flex flex-wrap items-baseline gap-x-6">
              <span className="text-2xl font-bold">{formatCotas(quantity)}</span>
              {!isPending && totalValue > 0 && (
                <span className="text-lg font-semibold">{Mask.formatCurrency(totalValue)}</span>
              )}
            </div>
          </div>

          {loading && (
            <div className="flex items-center justify-center gap-2 py-8 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin" />
              Carregando...
            </div>
          )}

          {failed && (
            <div className="py-8 text-center text-gray-500">Não foi possível carregar a lista.</div>
          )}

          {empty && (
            <div className="py-8 text-center text-gray-500">Nenhum registro para esta linha.</div>
          )}

          {!isPending && sales.length > 0 && (
            <div className="rounded-lg border bg-white dark:bg-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-primary/10 dark:bg-primary/20">
                    <TableRow>
                      <TableHead className="text-xs">ID</TableHead>
                      <TableHead className="text-xs">Bolão</TableHead>
                      <TableHead className="text-xs">Cliente</TableHead>
                      <TableHead className="text-xs">Vendedor</TableHead>
                      <TableHead className="text-xs">Unidade</TableHead>
                      <TableHead className="text-xs">Tipo</TableHead>
                      <TableHead className="text-xs text-center">Qtd</TableHead>
                      <TableHead className="text-xs">Valor</TableHead>
                      <TableHead className="text-xs text-center">Status</TableHead>
                      <TableHead className="text-xs text-center">Data/Hora</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sales.map((sale, index) => (
                      <TableRow
                        key={`${sale.sale_id}-${index}`}
                        className={cn(index % 2 === 0 ? "bg-white dark:bg-zinc-800" : "bg-primary/5 dark:bg-zinc-700/40")}
                      >
                        <TableCell className="text-sm font-medium">
                          <Link
                            to={`/vendas?sale_id=${sale.sale_id}`}
                            target="_blank"
                            title="Abrir a venda em nova aba"
                            className="inline-flex items-center gap-1 text-primary hover:underline"
                          >
                            #{sale.sale_id}
                            <ExternalLink className="h-3 w-3" />
                          </Link>
                        </TableCell>
                        <TableCell className="text-sm">{sale.product_name || "-"}</TableCell>
                        <TableCell className="text-sm">{sale.client?.name || "-"}</TableCell>
                        <TableCell className="text-sm">{sale.seller_name || "-"}</TableCell>
                        <TableCell className="text-sm">{sale.unity_name || "-"}</TableCell>
                        <TableCell className="text-sm">{sale.sale_type_name || "-"}</TableCell>
                        <TableCell className="text-sm text-center">{sale.quantity}</TableCell>
                        <TableCell className="text-sm">
                          {Mask.formatCurrency(sale.product_price_total)}
                        </TableCell>
                        <TableCell className="text-center">
                          <span
                            className={cn(
                              "px-2.5 py-0.5 rounded text-xs font-medium whitespace-nowrap",
                              getSaleStatusBadge(sale.sale_status?.id || 1)
                            )}
                          >
                            {sale.pre_sale_status_name || sale.sale_status?.name}
                          </span>
                        </TableCell>
                        <TableCell className="text-center text-sm whitespace-nowrap">
                          {formatDateTime(sale.sale_created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {truncated && (
                <p className="border-t px-4 py-2 text-xs text-gray-500">
                  Exibindo as 1.000 vendas mais recentes.
                </p>
              )}
            </div>
          )}

          {isPending && distributions.length > 0 && (
            <div className="rounded-lg border bg-white dark:bg-zinc-800 overflow-hidden">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader className="bg-primary/10 dark:bg-primary/20">
                    <TableRow>
                      <TableHead className="text-xs">ID</TableHead>
                      <TableHead className="text-xs">Tipo</TableHead>
                      <TableHead className="text-xs">Destino</TableHead>
                      <TableHead className="text-xs">Origem</TableHead>
                      <TableHead className="text-xs text-center">Qtd</TableHead>
                      <TableHead className="text-xs">Valor</TableHead>
                      <TableHead className="text-xs">Criado por</TableHead>
                      <TableHead className="text-xs text-center">Data/Hora</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {distributions.map((dist, index) => (
                      <TableRow
                        key={dist.distribution_id}
                        className={cn(index % 2 === 0 ? "bg-white dark:bg-zinc-800" : "bg-primary/5 dark:bg-zinc-700/40")}
                      >
                        <TableCell className="text-sm font-medium">#{dist.distribution_id}</TableCell>
                        <TableCell className="text-sm">{dist.distribution_type?.name || "-"}</TableCell>
                        <TableCell className="text-sm">
                          {dist.to_seller?.name || dist.to_unity?.name || "-"}
                        </TableCell>
                        <TableCell className="text-sm">
                          {dist.from_seller?.name || dist.from_unity?.name || "-"}
                        </TableCell>
                        <TableCell className="text-sm text-center">{dist.quota_quantity}</TableCell>
                        <TableCell className="text-sm">
                          {Mask.formatCurrency(dist.product_price_total || 0)}
                        </TableCell>
                        <TableCell className="text-sm">{dist.created_by || "-"}</TableCell>
                        <TableCell className="text-center text-sm whitespace-nowrap">
                          {formatDateTime(dist.distribution_created_at)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </AppDialogBody>
      </AppDialogContent>
    </Dialog>
  );
};
